/**
 * Shadow Findings Store (FG4: Governance Tijdbommen — Fase A: Shadow/Dry-run)
 *
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.B.
 *
 * THE PROBLEM
 * ===========
 * `dataSchemas.ts` is permissive (`return OK` for unregistered types) and
 * pedagogy checks default to coach-level. Flipping strictness without a ramp
 * is a CLIFF-EDGE: 30%+ of high-tier production questions could become Severity-70
 * blockers in one flip — content goes offline, not broken. The risk is
 * operational, not didactic: good questions rejected on MISSING METADATA.
 *
 * THE CONTRACT
 * ============
 * In shadow mode a stage EMITS what it WOULD block as a finding here, instead
 * of blocking. This yields the exact impact-radius (which families, tiers,
 * how many questions) BEFORE any enforcement decision.
 *
 * Anti-bandaid principle #4: no enforcement without prior observability.
 *
 * This is a plain module store (no React, no zustand) because the governance
 * pipeline runs outside the component tree. It exposes add/get/clear/subscribe
 * plus aggregate helpers for the admin impact-report.
 */

export type ShadowSource = 'pedagogy' | 'schema' | 'type' | 'content';
export interface ShadowFinding {
  /** Question this finding applies to. */
  questionId: string;
  /** Family id (e.g. 'GET-BREUK-BASIS'). */
  family: string;
  /** Module code (e.g. 'GET'). String to avoid a hard type import here. */
  module: string;
  /** Difficulty tier (1-16). */
  tier: number;
  /** Which stage produced the finding. */
  source: ShadowSource;
  /** Issue code that WOULD fire (e.g. 'CONTENT_DISTRACTOR_NO_EXPLANATION'). */
  wouldBlockReason: string;
  /** The severity this finding WOULD carry if enforced. */
  severity: number;
  /** Epoch ms when detected. */
  detectedAt: number;
}
let findings: ShadowFinding[] = [];
const listeners = new Set<() => void>();
function notify(): void {
  listeners.forEach(l => l());
}

/** Record a single shadow finding (a "would-block" that did NOT block). */
export function recordShadowFinding(finding: Omit<ShadowFinding, 'detectedAt'> & {
  detectedAt?: number;
}): void {
  findings.push({
    detectedAt: Date.now(),
    ...finding
  });
  notify();
}

/** Replace the entire findings set in one shot (used at the end of a pipeline run). */
export function setShadowFindings(next: ShadowFinding[]): void {
  findings = [...next];
  notify();
}

/** Read all current shadow findings (defensive copy). */
export function getShadowFindings(): ShadowFinding[] {
  return [...findings];
}

/** Clear all findings (e.g. before a fresh pipeline run). */
export function clearShadowFindings(): void {
  if (findings.length === 0) return;
  findings = [];
  notify();
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeShadowFindings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ─── Aggregate helpers for the admin impact-report ──────────────────────────

export interface ShadowImpactReport {
  total: number;
  byModule: Record<string, number>;
  bySource: Record<ShadowSource, number>;
  byReason: Record<string, number>;
  byTierBand: {
    basic: number;
    advanced: number;
    expert: number;
  };
  /** Families touched, sorted by finding count desc. */
  topFamilies: Array<{
    family: string;
    count: number;
  }>;
}
function tierBand(tier: number): 'basic' | 'advanced' | 'expert' {
  if (tier >= 13) return 'expert';
  if (tier >= 9) return 'advanced';
  return 'basic';
}

/**
 * Build the impact-report: "3.247 vragen, 412 zouden blocker worden,
 * breakdown per family/type". Feeds the FG4 Fase-A admin dashboard so the
 * team can make a data-driven enforcement decision.
 */
export function buildShadowImpactReport(input: ShadowFinding[] = findings): ShadowImpactReport {
  const byModule: Record<string, number> = {};
  const bySource: Record<ShadowSource, number> = {
    pedagogy: 0,
    schema: 0,
    type: 0,
    content: 0
  };
  const byReason: Record<string, number> = {};
  const byTierBand = {
    basic: 0,
    advanced: 0,
    expert: 0
  };
  const familyCounts: Record<string, number> = {};
  for (const f of input) {
    byModule[f.module] = (byModule[f.module] ?? 0) + 1;
    bySource[f.source] = (bySource[f.source] ?? 0) + 1;
    byReason[f.wouldBlockReason] = (byReason[f.wouldBlockReason] ?? 0) + 1;
    byTierBand[tierBand(f.tier)] += 1;
    familyCounts[f.family] = (familyCounts[f.family] ?? 0) + 1;
  }
  const topFamilies = Object.entries(familyCounts).map(([family, count]) => ({
    family,
    count
  })).sort((a, b) => b.count - a.count);
  return {
    total: input.length,
    byModule,
    bySource,
    byReason,
    byTierBand,
    topFamilies
  };
}